/**
 * COS App Center — the operating system's app library.
 *
 * Not a settings page. This is the central place where an organization
 * assembles its own Community Operating System by installing, enabling,
 * configuring, and licensing COS Applications.
 *
 * Two sections:
 *   1. Installed Applications — everything currently mounted on this deploy.
 *   2. Available Applications — everything the platform *could* run
 *      (marketplace_visible + not-yet-installed apps, including coming-soon).
 *
 * Every card reads its manifest from `/api/cos/apps`. No app-specific code
 * lives in this file — that's the whole point of self-describing manifests.
 */
import React, { useEffect, useState, useMemo } from "react";
import * as LucideIcons from "lucide-react";
import { AppShell } from "../components/cod/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { cosAppsApi } from "../lib/api";
import { toast } from "sonner";
import { AppConfigEditor } from "../components/cos/AppConfigEditor";
import {
  Box, CheckCircle2, XCircle, Sparkles, Info, Search, RefreshCw,
  Package, Shield, Layers, Cpu, ExternalLink, Settings,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────
   Presentational helpers
   ───────────────────────────────────────────────────────────────────── */
const CATEGORY_LABELS = {
  ai: "AI", crm: "CRM", workforce: "Workforce",
  community: "Community", analytics: "Analytics",
};

const CATEGORY_TINT = {
  ai: { bg: "#F5F0FF", fg: "#5B21B6", stripe: "#7C3AED" },
  crm: { bg: "#FEF3C7", fg: "#92400E", stripe: "#B8841A" },
  workforce: { bg: "#E8EEF7", fg: "#14366B", stripe: "#14366B" },
  community: { bg: "#FDEAEC", fg: "#7F1D1D", stripe: "#C8102E" },
  analytics: { bg: "#DCFCE7", fg: "#166534", stripe: "#16A34A" },
};

const LICENSE_LABEL = { free: "Free", paid: "Paid", enterprise: "Enterprise" };

function priceLabel(m) {
  if (m.license_tier === "free") return "Free";
  if (m.license_tier === "enterprise") return "Enterprise · contact sales";
  if (m.pricing_monthly) return `$${m.pricing_monthly}/mo`;
  return "Contact sales";
}

function IconGlyph({ name, size = 22, color }) {
  const Cmp = LucideIcons[name] || Box;
  return <Cmp size={size} color={color} strokeWidth={1.6} />;
}

/* ─────────────────────────────────────────────────────────────────────
   AppCard — the single unit of the App Center
   ───────────────────────────────────────────────────────────────────── */
function AppCard({ row, onOpen, onQuickAction, busy }) {
  const m = row.manifest;
  const tint = CATEGORY_TINT[m.category] || CATEGORY_TINT.workforce;
  const comingSoon = m.availability === "coming_soon";
  const installed = row.installed;
  const enabled = row.enabled;
  const restart = row.enabled && !row.mounted;

  return (
    <Card
      data-testid={`cos-app-card-${row.id}`}
      className="cod-card-elev overflow-hidden cursor-pointer border-slate-200"
      onClick={() => onOpen(row.id)}
    >
      {/* Category stripe */}
      <div style={{ height: 4, background: tint.stripe }} />
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div
            className="rounded-xl p-3 shrink-0"
            style={{ background: tint.bg, color: tint.fg }}
          >
            <IconGlyph name={m.icon} size={24} color={tint.fg} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-serif text-lg text-slate-900 truncate">
                {row.name}
              </h3>
              <Badge
                variant="outline"
                className="text-[10px] tracking-wider uppercase"
                style={{ borderColor: tint.stripe, color: tint.fg }}
              >
                {CATEGORY_LABELS[m.category] || m.category}
              </Badge>
              {comingSoon && (
                <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 text-[10px]">
                  Coming soon
                </Badge>
              )}
              {installed && !comingSoon && (
                <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100 text-[10px] gap-1">
                  <CheckCircle2 size={10}/> Installed
                </Badge>
              )}
              {installed && !enabled && (
                <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 text-[10px]">
                  Disabled
                </Badge>
              )}
              {restart && (
                <Badge className="bg-blue-100 text-blue-900 hover:bg-blue-100 text-[10px]">
                  Restart needed
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-600 mt-1.5 leading-snug line-clamp-2">
              {m.tagline || row.name}
            </p>
            <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
              <span>v{row.version}</span>
              <span className="text-slate-300">·</span>
              <span>{m.publisher}</span>
              <span className="text-slate-300">·</span>
              <span className="font-medium" style={{ color: tint.fg }}>
                {priceLabel(m)}
              </span>
            </div>
          </div>
        </div>

        {/* Compact facts row */}
        <div className="grid grid-cols-3 gap-2 mt-4 text-[11px] text-slate-500">
          <FactCell icon={Cpu} label="AI agents" value={m.ai_agents?.length || 0} />
          <FactCell icon={Layers} label="Routes" value={m.routes?.length || 0} />
          <FactCell icon={Shield} label="Permissions" value={m.permissions?.length || 0} />
        </div>

        {/* Quick actions */}
        <div
          className="flex items-center gap-2 mt-4"
          onClick={(e) => e.stopPropagation()}
        >
          {comingSoon && (
            <Button
              variant="outline" size="sm" disabled
              className="w-full"
              data-testid={`cos-app-${row.id}-waitlist`}
            >
              <Sparkles size={14} className="mr-1.5"/> Notify me
            </Button>
          )}
          {!comingSoon && !installed && (
            <Button
              size="sm" className="w-full cod-bg-red"
              disabled={busy === row.id}
              onClick={() => onQuickAction(row.id, "install")}
              data-testid={`cos-app-${row.id}-install`}
            >
              <Package size={14} className="mr-1.5"/> Install
            </Button>
          )}
          {!comingSoon && installed && enabled && (
            <Button
              size="sm" variant="outline"
              disabled={busy === row.id}
              onClick={() => onQuickAction(row.id, "disable")}
              data-testid={`cos-app-${row.id}-disable`}
            >
              Disable
            </Button>
          )}
          {!comingSoon && installed && !enabled && (
            <Button
              size="sm" className="cod-bg-red"
              disabled={busy === row.id}
              onClick={() => onQuickAction(row.id, "enable")}
              data-testid={`cos-app-${row.id}-enable`}
            >
              Enable
            </Button>
          )}
          {!comingSoon && installed && (
            <Button
              size="sm" variant="ghost"
              onClick={() => onOpen(row.id)}
              data-testid={`cos-app-${row.id}-details`}
            >
              <Info size={14} className="mr-1"/> Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function FactCell({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={12} className="text-slate-400" />
      <span className="font-semibold text-slate-700">{value}</span>
      <span className="uppercase tracking-wider">{label}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Detail modal
   ───────────────────────────────────────────────────────────────────── */
function AppDetailModal({ row, onClose, onAction, busy, onConfigSaved }) {
  const [saving, setSaving] = useState(false);
  if (!row) return null;
  const m = row.manifest;
  const tint = CATEGORY_TINT[m.category] || CATEGORY_TINT.workforce;
  const hasConfig = (row.config_schema || []).length > 0;

  const saveConfig = async (changed) => {
    setSaving(true);
    try {
      await cosAppsApi.updateConfig(row.id, changed);
      toast.success(`${row.name} configuration saved`);
      onConfigSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-4">
            <div
              className="rounded-xl p-3 shrink-0"
              style={{ background: tint.bg, color: tint.fg }}
            >
              <IconGlyph name={m.icon} size={30} color={tint.fg} />
            </div>
            <div className="flex-1">
              <DialogTitle className="font-serif text-2xl">{row.name}</DialogTitle>
              <DialogDescription className="text-sm text-slate-500 mt-1">
                {m.publisher} · v{row.version} · {CATEGORY_LABELS[m.category]} · {priceLabel(m)}
              </DialogDescription>
              <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                {m.long_description || m.description}
              </p>
            </div>
          </div>
        </DialogHeader>

        <Separator className="my-4"/>

        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview" data-testid="cos-detail-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="configure" data-testid="cos-detail-tab-configure"
                         disabled={!hasConfig || !row.installed}>
              <Settings size={13} className="mr-1"/> Configure
              {hasConfig && <Badge variant="outline" className="ml-2 text-[10px]">
                {row.config_schema.length}
              </Badge>}
            </TabsTrigger>
            <TabsTrigger value="manifest" data-testid="cos-detail-tab-manifest">Manifest</TabsTrigger>
            <TabsTrigger value="license" data-testid="cos-detail-tab-license">License</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <FieldGroup title="Dependencies">
              {m.dependencies?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {m.dependencies.map(d => (
                    <Badge key={d} variant="outline">{d}</Badge>
                  ))}
                </div>
              ) : <EmptyRow>No upstream dependencies</EmptyRow>}
            </FieldGroup>

            <FieldGroup title="AI agents">
              {m.ai_agents?.length ? (
                <ul className="space-y-2">
                  {m.ai_agents.map((a, i) => (
                    <li key={i} className="text-sm border border-slate-200 rounded-lg p-3 bg-slate-50">
                      <div className="font-semibold text-slate-900">
                        {a.name} <span className="text-slate-500 font-normal">· {a.model}</span>
                      </div>
                      <div className="text-slate-600 text-xs mt-1">{a.purpose}</div>
                    </li>
                  ))}
                </ul>
              ) : <EmptyRow>None</EmptyRow>}
            </FieldGroup>

            <div className="grid grid-cols-2 gap-4">
              <FieldGroup title="Permissions">
                {m.permissions?.length
                  ? <ChipList items={m.permissions}/>
                  : <EmptyRow>None</EmptyRow>}
              </FieldGroup>
              <FieldGroup title="Owned collections">
                {m.collections?.length
                  ? <ChipList items={m.collections}/>
                  : <EmptyRow>None</EmptyRow>}
              </FieldGroup>
            </div>

            <FieldGroup title="Routes">
              {m.routes?.length
                ? <ChipList items={m.routes} mono/>
                : <EmptyRow>None</EmptyRow>}
            </FieldGroup>

            {m.background_jobs?.length > 0 && (
              <FieldGroup title="Background jobs">
                <ul className="space-y-1 text-sm">
                  {m.background_jobs.map((j, i) => (
                    <li key={i} className="text-slate-700">
                      <span className="font-semibold">{j.name}</span>
                      <span className="text-slate-500"> — {j.schedule}</span>
                      {j.description && <div className="text-xs text-slate-500 ml-1">{j.description}</div>}
                    </li>
                  ))}
                </ul>
              </FieldGroup>
            )}

            {m.config_options?.length > 0 && (
              <FieldGroup title="Configuration options">
                <ul className="space-y-1 text-sm">
                  {m.config_options.map((c, i) => (
                    <li key={i} className="text-slate-700">
                      <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{c.key}</code>
                      <span className="text-slate-500 text-xs ml-2">{c.type} · default: {String(c.default ?? "—")}</span>
                      {c.description && <div className="text-xs text-slate-500 mt-0.5">{c.description}</div>}
                    </li>
                  ))}
                </ul>
              </FieldGroup>
            )}

            {m.tags?.length > 0 && (
              <FieldGroup title="Tags">
                <ChipList items={m.tags}/>
              </FieldGroup>
            )}
          </TabsContent>

          <TabsContent value="configure" className="mt-4">
            <AppConfigEditor
              schema={row.config_schema}
              effective={row.config_effective}
              onSave={saveConfig}
              saving={saving}
            />
          </TabsContent>

          <TabsContent value="manifest" className="mt-4">
            <pre className="text-[11px] bg-slate-950 text-slate-100 rounded-lg p-4 overflow-x-auto max-h-96"
                 data-testid="cos-detail-manifest-json">
              {JSON.stringify(m, null, 2)}
            </pre>
          </TabsContent>

          <TabsContent value="license" className="mt-4 space-y-3">
            <FieldGroup title="License tier">
              <Badge className="text-sm">{LICENSE_LABEL[m.license_tier] || m.license_tier}</Badge>
              {m.pricing_monthly && (
                <div className="text-sm text-slate-600 mt-2">
                  Monthly: <b>${m.pricing_monthly}</b>
                  {m.pricing_annual && <> · Annual: <b>${m.pricing_annual}</b></>}
                </div>
              )}
              {m.trial_available && <div className="text-xs text-slate-500 mt-1">Trial available</div>}
            </FieldGroup>
            <FieldGroup title="Current license status">
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-sm">
                <div className="flex items-center gap-2">
                  {row.license?.valid
                    ? <CheckCircle2 size={16} className="text-emerald-600"/>
                    : <XCircle size={16} className="text-red-600"/>}
                  <span className="font-semibold">
                    {row.license?.valid ? "Valid" : "Invalid"}
                  </span>
                  <span className="text-xs text-slate-500">· source: {row.license?.source}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{row.license?.reason}</div>
                {row.license?.entitlements?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {row.license.entitlements.map(e => (
                      <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </FieldGroup>
            {m.docs_url && (
              <a href={m.docs_url} target="_blank" rel="noreferrer"
                 className="cod-link text-sm inline-flex items-center gap-1 mt-2">
                Documentation <ExternalLink size={12}/>
              </a>
            )}
          </TabsContent>
        </Tabs>

        <Separator className="my-4"/>

        {/* Action bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {row.installed && !row.mounted && (
              <span className="text-blue-700">Restart the backend to activate changes.</span>
            )}
            {row.installed && row.mounted && (
              <span className="text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle2 size={12}/> Mounted and serving traffic.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {m.availability !== "coming_soon" && !row.installed && (
              <Button className="cod-bg-red" disabled={busy}
                      onClick={() => onAction(row.id, "install")}
                      data-testid="cos-detail-install-btn">
                Install
              </Button>
            )}
            {row.installed && row.enabled && !row.manifest.core && (
              <Button variant="outline" disabled={busy}
                      onClick={() => onAction(row.id, "disable")}
                      data-testid="cos-detail-disable-btn">
                Disable
              </Button>
            )}
            {row.installed && !row.enabled && (
              <Button className="cod-bg-red" disabled={busy}
                      onClick={() => onAction(row.id, "enable")}
                      data-testid="cos-detail-enable-btn">
                Enable
              </Button>
            )}
            {row.installed && !row.manifest.core && (
              <Button variant="ghost" disabled={busy}
                      onClick={() => onAction(row.id, "uninstall")}
                      data-testid="cos-detail-uninstall-btn">
                Uninstall
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FieldGroup = ({ title, children }) => (
  <div>
    <div className="cod-eyebrow mb-1.5">{title}</div>
    {children}
  </div>
);
const EmptyRow = ({ children }) => (
  <div className="text-sm text-slate-500 italic">{children}</div>
);
const ChipList = ({ items, mono }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((x, i) => (
      <span key={i} className={`text-[11px] px-2 py-1 rounded ${mono ? "font-mono" : ""}`}
            style={{ background: "#F1F5F9", color: "#334155" }}>
        {x}
      </span>
    ))}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────────────── */
export default function COSAppCenter() {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  const load = async () => {
    try {
      const d = await cosAppsApi.list();
      setData(d);
    } catch (e) {
      toast.error("Failed to load App Center");
    }
  };
  useEffect(() => { load(); }, []);

  const filter = (rows) => {
    let out = rows;
    if (query) {
      const q = query.toLowerCase();
      out = out.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        (r.manifest.tagline || "").toLowerCase().includes(q) ||
        (r.manifest.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    if (category !== "all") out = out.filter(r => r.manifest.category === category);
    return out;
  };

  const installed = useMemo(() => data ? filter(data.installed) : [], [data, query, category]);
  const available = useMemo(() => data ? filter(data.available) : [], [data, query, category]);
  const openRow = data?.all.find(r => r.id === openId) || null;

  const action = async (id, kind) => {
    setBusy(id);
    try {
      if (kind === "install") await cosAppsApi.install(id);
      if (kind === "uninstall") await cosAppsApi.uninstall(id);
      if (kind === "enable") await cosAppsApi.enable(id);
      if (kind === "disable") await cosAppsApi.disable(id);
      toast.success(`${kind} ok — restart the backend to activate.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${kind}`);
    } finally {
      setBusy(null);
    }
  };

  const cats = data
    ? Array.from(new Set(data.all.map(r => r.manifest.category)))
    : [];

  return (
    <AppShell>
      <div className="space-y-8">
        {/* Hero */}
        <header className="border-b border-slate-200 pb-6">
          <div className="cod-eyebrow" style={{ color: "var(--cod-red)" }}>
            Community Operating System
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-2">
            COS App Center
          </h1>
          <p className="text-slate-600 mt-3 max-w-2xl leading-relaxed">
            Organizations don't just use COS — they <em>assemble</em> their own operating system by
            installing the applications they need. Chicago Opportunity Desk runs the flagship
            deployment.
          </p>
          {data?.platform && (
            <div className="flex flex-wrap gap-6 mt-5 text-sm text-slate-500">
              <StatPill label="Platform" value={data.platform.short_name} accent/>
              <StatPill label="Installed" value={data.platform.installed_count} accent/>
              <StatPill label="Available" value={data.platform.available_count}/>
              <StatPill label="Total Apps" value={data.platform.total_apps}/>
              <StatPill label="Flagship" value={data.platform.flagship_organization}/>
            </div>
          )}
        </header>

        {/* Controls */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search applications, tags, publishers…"
              className="pl-9"
              data-testid="cos-search-input"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <CatChip active={category==="all"} onClick={()=>setCategory("all")} label="All"/>
            {cats.map(c => (
              <CatChip key={c} active={category===c} onClick={()=>setCategory(c)}
                       label={CATEGORY_LABELS[c] || c}/>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="cos-refresh-btn">
            <RefreshCw size={14} className="mr-1"/> Refresh
          </Button>
        </div>

        {/* Installed */}
        <section data-testid="cos-installed-section">
          <SectionHeader title="Installed Applications"
            hint="Mounted and serving traffic on this deployment"
            count={installed.length}/>
          {installed.length === 0 ? (
            <EmptyState msg="No installed applications match your filters."/>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {installed.map(row => (
                <AppCard key={row.id} row={row}
                         onOpen={setOpenId}
                         onQuickAction={action}
                         busy={busy}/>
              ))}
            </div>
          )}
        </section>

        {/* Available */}
        <section data-testid="cos-available-section">
          <SectionHeader title="Available Applications"
            hint="Ready to install, or on the roadmap"
            count={available.length}/>
          {available.length === 0 ? (
            <EmptyState msg="No available applications match your filters."/>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {available.map(row => (
                <AppCard key={row.id} row={row}
                         onOpen={setOpenId}
                         onQuickAction={action}
                         busy={busy}/>
              ))}
            </div>
          )}
        </section>

        <AppDetailModal
          row={openRow}
          onClose={() => setOpenId(null)}
          onAction={async (id, k) => { await action(id, k); if (k==="uninstall") setOpenId(null); }}
          onConfigSaved={load}
          busy={busy === openId}
        />
      </div>
    </AppShell>
  );
}

const StatPill = ({ label, value, accent }) => (
  <div className="flex flex-col">
    <span className="cod-eyebrow" style={{ color: accent ? "var(--cod-red)" : "var(--cod-navy)" }}>
      {label}
    </span>
    <span className="font-serif text-2xl text-slate-900">{value}</span>
  </div>
);

const CatChip = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    data-testid={`cos-cat-${label.toLowerCase()}`}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
      active
        ? "text-white border-transparent"
        : "text-slate-600 border-slate-300 hover:border-slate-500"
    }`}
    style={active ? { background: "var(--cod-navy)" } : {}}
  >
    {label}
  </button>
);

const SectionHeader = ({ title, hint, count }) => (
  <div className="flex items-baseline gap-3 mb-4">
    <h2 className="font-serif text-2xl text-slate-900">{title}</h2>
    <span className="text-slate-400 text-sm">{count}</span>
    <div className="cod-rule-red hidden md:block"/>
    <span className="text-xs text-slate-500 hidden md:inline">{hint}</span>
  </div>
);

const EmptyState = ({ msg }) => (
  <div className="text-center py-10 text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg">
    {msg}
  </div>
);
